import { immutableSort } from './array.utils';
import { normalizeEmail } from './string.utils';

export type AccessRightPermissionComparisonInput = {
  permissions: {
    canManageAccessRights?: boolean;
    canManageStudents?: boolean;
    canManageCompetences?: boolean;
    canManagePerformanceAssessments?: boolean;
    subjects?: Array<{ id: number }> | null;
  };
  schoolSubjects: Array<{ id: number }>;
};

export type AccessRightComparisonInput = AccessRightPermissionComparisonInput & {
  user: {
    id?: number | null;
    email?: string | null;
  };
  userRoleType?: string | null;
  isInvitation?: boolean;
};

export type AccessRightComparisonOptions = {
  compareUserRoleType?: boolean;
};

export const getAccessRightUserKey = (accessRight: AccessRightComparisonInput): string => {
  const normalizedEmail = normalizeEmail(accessRight.user.email);
  if (normalizedEmail) return normalizedEmail;

  return `${accessRight.isInvitation ? 'invitation' : 'user'}:${accessRight.user.id ?? ''}`;
};

export const getSortedAccessRightSubjectIds = (accessRight: AccessRightPermissionComparisonInput): number[] => {
  const subjects =
    accessRight.permissions.subjects !== undefined
      ? (accessRight.permissions.subjects ?? [])
      : accessRight.schoolSubjects;
  return immutableSort(
    subjects.map((schoolSubject) => schoolSubject.id),
    (a, b) => a - b,
  );
};

export const hasEqualPermissionsAndSubjectAccess = (
  a: AccessRightPermissionComparisonInput,
  b: AccessRightPermissionComparisonInput,
): boolean => {
  const aSubjectIds = getSortedAccessRightSubjectIds(a);
  const bSubjectIds = getSortedAccessRightSubjectIds(b);
  return (
    a.permissions.canManageAccessRights === b.permissions.canManageAccessRights &&
    a.permissions.canManageStudents === b.permissions.canManageStudents &&
    a.permissions.canManagePerformanceAssessments === b.permissions.canManagePerformanceAssessments &&
    a.permissions.canManageCompetences === b.permissions.canManageCompetences &&
    aSubjectIds.length === bSubjectIds.length &&
    aSubjectIds.every((id, index) => id === bSubjectIds[index])
  );
};

export const hasEqualAccessRight = (
  a: AccessRightComparisonInput,
  b: AccessRightComparisonInput,
  options: AccessRightComparisonOptions = {},
): boolean => {
  return (
    hasEqualPermissionsAndSubjectAccess(a, b) && (!options.compareUserRoleType || a.userRoleType === b.userRoleType)
  );
};

export const haveEqualAccessRights = (
  a: AccessRightComparisonInput[],
  b: AccessRightComparisonInput[],
  options: AccessRightComparisonOptions = {},
): boolean => {
  if (a.length !== b.length) return false;

  const aByKey = new Map(a.map((accessRight) => [getAccessRightUserKey(accessRight), accessRight]));
  const bByKey = new Map(b.map((accessRight) => [getAccessRightUserKey(accessRight), accessRight]));
  if (aByKey.size !== a.length || bByKey.size !== b.length || aByKey.size !== bByKey.size) return false;

  for (const [key, accessRight] of aByKey) {
    const matchingAccessRight = bByKey.get(key);
    if (!matchingAccessRight || !hasEqualAccessRight(accessRight, matchingAccessRight, options)) {
      return false;
    }
  }

  return true;
};
